/* Markdown rendering and prose statistics. */

export const escapeHtml = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function inline(s) {
  return escapeHtml(s)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');
}

export function syllables(w) {
  w = w.toLowerCase().replace(/[^a-z]/g, '');
  if (!w) return 0;
  if (w.length <= 3) return 1;
  const m = w.replace(/e$/, '').match(/[aeiouy]{1,2}/g);
  return m ? m.length : 1;
}

// Words ending in -ly that are not adverbs.
export const NOT_ADVERBS = /^(only|family|supply|reply|italy|july|early|daily|assembly|belly|fly|apply|ally|jelly|holy|ugly|likely|monopoly)$/i;

/**
 * Render markdown to HTML while collecting prose statistics: long sentences,
 * adverbs and passive constructions are highlighted inline.
 */
export function analyze(text) {
  const res = { adverbs: 0, passive: 0, hard: 0, vhard: 0, sents: 0, tw: 0, tsyl: 0 };

  const decorate = (sentence) => {
    let html = inline(sentence);
    html = html.replace(/\b(am|is|are|was|were|be|been|being)\s+([a-z]+(?:ed|en|wn))\b/gi, (m) => {
      res.passive++;
      return '<span class="mk-passive">' + m + '</span>';
    });
    html = html.replace(/\b([A-Za-z]+ly)\b/g, (m, w) => {
      if (NOT_ADVERBS.test(w)) return m;
      res.adverbs++;
      return '<span class="mk-adverb">' + m + '</span>';
    });

    const ws = sentence.match(/\S+/g) || [];
    if (ws.length) {
      res.sents++;
      res.tw += ws.length;
      for (const w of ws) res.tsyl += syllables(w);
    }
    if (ws.length >= 26) { res.vhard++; return '<span class="mk-vhard">' + html + '</span>'; }
    if (ws.length >= 15) { res.hard++;  return '<span class="mk-hard">'  + html + '</span>'; }
    return html;
  };

  const prose = (t) =>
    (t.match(/[^.!?]+[.!?]+[\)"'”’]*\s*|[^.!?]+$/g) || [t]).map(decorate).join('');

  let html = '', inList = false;
  for (const line of (text || '').split(/\n/)) {
    if (/^\s*[-*]\s+/.test(line)) {
      if (!inList) { html += '<ul>'; inList = true; }
      html += '<li>' + prose(line.replace(/^\s*[-*]\s+/, '')) + '</li>';
      continue;
    }
    if (inList) { html += '</ul>'; inList = false; }

    if (/^###\s+/.test(line))      html += '<h3>' + inline(line.replace(/^###\s+/, '')) + '</h3>';
    else if (/^##\s+/.test(line))  html += '<h2>' + inline(line.replace(/^##\s+/, '')) + '</h2>';
    else if (/^#\s+/.test(line))   html += '<h1>' + inline(line.replace(/^#\s+/, '')) + '</h1>';
    else if (/^>\s?/.test(line))   html += '<blockquote>' + prose(line.replace(/^>\s?/, '')) + '</blockquote>';
    else if (/\S/.test(line))      html += '<p>' + prose(line) + '</p>';
  }
  if (inList) html += '</ul>';

  res.html = html || '<p class="nothing">Nothing written yet. Switch to Write and begin.</p>';

  // Flesch–Kincaid grade level, clamped to a sane range.
  if (res.sents && res.tw) {
    const g = Math.round(0.39 * (res.tw / res.sents) + 11.8 * (res.tsyl / res.tw) - 15.59);
    res.grade = Math.max(1, Math.min(18, g));
  } else {
    res.grade = 0;
  }
  return res;
}

export const gradeColor = g => (!g ? 'var(--muted-3)' : g <= 9 ? 'var(--good)' : g <= 12 ? 'var(--warn)' : 'var(--danger)');
export const gradeNote = g => (!g ? 'Write something first.'
  : g <= 6 ? 'Very readable.' : g <= 9 ? 'Good.' : g <= 12 ? 'A bit dense.' : 'Hard going.');
